import React from "react";

interface IProps {
    text: string | null | undefined;
    className?: string;
    // volitelně tag, default div
    as?: keyof JSX.IntrinsicElements;
}

/**
 * Escape HTML characters so nothing vložené do textu může být interpretováno jako HTML.
 */
function escapeHtml(s: string): string {
    return s
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
}

/**
 * Parse line for *bold* markers and return array of React nodes.
 * - Only supports simple asterisk-based bold: *text*
 * - Unpaired asterisks remain literal.
 */
function parseLineToNodes(line: string, keyPrefix = ""): React.ReactNode[] {
    // split on groups that look like *...* (non-greedy)
    // capturing group keeps the matched *...* pieces
    const parts = line.split(/(\*(?:.+?)\*)/g);
    const nodes: React.ReactNode[] = [];

    parts.forEach((part, idx) => {
        if (!part) return;
        const boldMatch = part.match(/^\*(.+)\*$/s); // s -> allow newlines inside (unlikely here)
        if (boldMatch) {
            const inner = escapeHtml(boldMatch[1]);
            nodes.push(
                    <strong key={`${keyPrefix}-b-${idx}`} dangerouslySetInnerHTML={{ __html: inner }} />
            );
        } else {
            // plain text (escaped)
            nodes.push(<span key={`${keyPrefix}-t-${idx}`}>{escapeHtml(part)}</span>);
        }
    });

    return nodes;
}

/**
 * SafeFormattedText
 * - converts \n -> <br/>
 * - *bold* -> <strong>...</strong>
 * - other HTML characters are escaped
 */
export const TextFormatted: React.FC<IProps> = ({ text, className, as = "span" }) => {
    const Tag = as as any;

    if (!text) {
        return <Tag className={className} />;
    }

    // split text to rows, empty rows are preserved
    const lines = text.split("\n");

    const nodes: React.ReactNode[] = [];

    lines.forEach((line, i) => {
        const lineNodes = parseLineToNodes(line, `ln${i}`);
        nodes.push(...lineNodes);

        // add line break between rows, except after last line
        if (i < lines.length - 1) {
            nodes.push(<br key={`br-${i}`} />);
        }
    });

    return <Tag className={className}>{nodes}</Tag>;
};

export default TextFormatted;